import { useEffect, useState } from 'react';
import type {
  AgentTrace,
  AnthropicContentBlock,
  ArchivedMessage,
} from '../../lib/artefacts-api';
import type { ArtefactsState } from '../../hooks/useBriefArtefacts';
import { useEffectiveTimeZone, formatInstant } from '../../lib/time-format';

/**
 * Agent Trace panel — renders the Anthropic message archive as a
 * turn-by-turn conversation log (Phase 5d-1 follow-up commit 5).
 *
 * Each message is one card; content blocks (text, tool_use, tool_result)
 * render with distinct styling so staff can scan the agent's reasoning,
 * see which tools fired, and inspect the results. Long traces paginate
 * at 20 messages per page per ADR-029 § Anticipated edge cases — keeps
 * the DOM lean for runs with hundreds of tool turns.
 */
const PAGE_SIZE = 20;

interface Props {
  state: ArtefactsState<AgentTrace>;
  noAgentRunYet: boolean;
  onExpand: () => void;
  lastAssessedAt: string | null;
}

export function AgentTracePanel({ state, noAgentRunYet, onExpand, lastAssessedAt }: Props) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const tz = useEffectiveTimeZone();

  useEffect(() => {
    if (open && state.status === 'idle' && !noAgentRunYet) {
      onExpand();
    }
  }, [open, state.status, noAgentRunYet, onExpand]);

  return (
    <section className="portal-brief-section">
      <button
        type="button"
        className="portal-collapsible"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>
          🤖 Agent Conversation Trace{' '}
          {lastAssessedAt ? `· last run ${formatInstant(lastAssessedAt, tz, 'date').text}` : ''}
        </span>
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="portal-collapsible-body">
          <Body state={state} noAgentRunYet={noAgentRunYet} page={page} setPage={setPage} />
        </div>
      )}
    </section>
  );
}

function Body({
  state,
  noAgentRunYet,
  page,
  setPage,
}: {
  state: Props['state'];
  noAgentRunYet: boolean;
  page: number;
  setPage: (p: number) => void;
}) {
  if (noAgentRunYet || state.status === 'no-agent-run-yet') {
    return (
      <p className="portal-help">
        No agent conversation trace for this Brief — the pow-agent has not run yet. Real
        submissions write the full Anthropic message array
        (<code>messages[]</code> + <code>toolCalls[]</code> + outcome) at run completion.
      </p>
    );
  }
  if (state.status === 'loading' || state.status === 'idle') {
    return <p className="portal-help">Loading agent trace…</p>;
  }
  if (state.status === 'error') {
    return <p className="portal-help">Could not load agent trace: {state.error}</p>;
  }
  const trace = state.data;
  if (!trace) return null;

  const totalPages = Math.max(1, Math.ceil(trace.messages.length / PAGE_SIZE));
  const start = page * PAGE_SIZE;
  const slice = trace.messages.slice(start, start + PAGE_SIZE);

  return (
    <div className="portal-agent-trace">
      <div className="portal-agent-trace-meta">
        <span>
          <strong>{trace.messages.length}</strong> messages · {trace.toolCalls.length} tool calls ·
          outcome: <code>{trace.outcome.decision}</code> · score{' '}
          <code>{trace.outcome.score}</code>
        </span>
        <span className="portal-agent-trace-trace-id">traceId: <code>{trace.traceId}</code></span>
      </div>
      <ol className="portal-agent-trace-list" start={start + 1}>
        {slice.map((msg, idx) => (
          <MessageCard key={start + idx} message={msg} index={start + idx} />
        ))}
      </ol>
      {totalPages > 1 && (
        <div className="portal-agent-trace-pagination">
          <button type="button" className="portal-link-button-secondary" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>
            ← Previous {PAGE_SIZE}
          </button>
          <span>
            Page {page + 1} of {totalPages}
          </span>
          <button
            type="button"
            className="portal-link-button-secondary"
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1}
          >
            Next {PAGE_SIZE} →
          </button>
        </div>
      )}
    </div>
  );
}

function MessageCard({ message, index }: { message: ArchivedMessage; index: number }) {
  const roleClass = `portal-agent-message portal-agent-message-${message.role}`;
  return (
    <li className={roleClass}>
      <div className="portal-agent-message-header">
        <span className="portal-agent-message-role">{message.role}</span>
        <span className="portal-agent-message-index">#{index + 1}</span>
      </div>
      <div className="portal-agent-message-body">
        {typeof message.content === 'string' ? (
          <p className="portal-agent-message-text">{message.content}</p>
        ) : (
          message.content.map((block, blockIdx) => (
            <ContentBlock key={blockIdx} block={block} />
          ))
        )}
      </div>
    </li>
  );
}

function ContentBlock({ block }: { block: AnthropicContentBlock }) {
  switch (block.type) {
    case 'text':
      return <p className="portal-agent-message-text">{(block as { text: string }).text}</p>;
    case 'tool_use': {
      const b = block as { id: string; name: string; input: unknown };
      return (
        <div className="portal-agent-tool-use">
          <span className="portal-agent-tool-use-label">tool_use</span>{' '}
          <code>{b.name}</code>
          <pre className="portal-agent-tool-use-input">{JSON.stringify(b.input, null, 2)}</pre>
        </div>
      );
    }
    case 'tool_result': {
      const b = block as { tool_use_id: string; content: unknown; is_error?: boolean };
      return (
        <div
          className={`portal-agent-tool-result${b.is_error ? ' portal-agent-tool-result-error' : ''}`}
        >
          <span className="portal-agent-tool-result-label">tool_result</span>
          <pre className="portal-agent-tool-result-content">
            {typeof b.content === 'string' ? b.content : JSON.stringify(b.content, null, 2)}
          </pre>
        </div>
      );
    }
    default:
      return (
        <div className="portal-agent-tool-use">
          <span className="portal-agent-tool-use-label">{block.type}</span>
          <pre>{JSON.stringify(block, null, 2)}</pre>
        </div>
      );
  }
}

